import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day:   'numeric',
    year:  'numeric',
  });
}

export default function ReorderModal({ show, loading, data, onClose, onConfirm }) {
  const [quantities, setQuantities]       = useState({});
  const [validationError, setValidationError] = useState('');

  // Initialise quantities from data whenever data changes
  useEffect(() => {
    if (data?.items) {
      const init = {};
      data.items.forEach(item => {
        init[item.productID] = item.isActive ? item.previousQty : 0;
      });
      setQuantities(init);
    }
    setValidationError('');
  }, [data]);

  // Clear validation error when modal is closed
  useEffect(() => {
    if (!show) setValidationError('');
  }, [show]);

  const handleQtyChange = (productID, value) => {
    const qty = Math.max(0, parseInt(value) || 0);
    setQuantities(prev => ({ ...prev, [productID]: qty }));
    setValidationError('');
  };

  const handleConfirm = () => {
    const toAdd = (data?.items || [])
      .filter(item => item.isActive && (quantities[item.productID] || 0) > 0)
      .map(item => ({ productID: item.productID, quantity: quantities[item.productID] }));

    if (!toAdd.length) {
      setValidationError('Please set a quantity greater than 0 for at least one available item.');
      return;
    }

    setValidationError('');
    onConfirm(toAdd);
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ zIndex: 1040 }}
      />

      {/* Modal */}
      <div
        className="modal fade show"
        style={{ display: 'block', zIndex: 1050 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reorder-modal-title"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">

            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title" id="reorder-modal-title">
                {data ? `Reorder from ${formatDate(data.orderDate)}` : 'Reorder Last Purchase'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div className="modal-body">

              {/* Loading state */}
              {loading && (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-secondary" role="status">
                    <span className="visually-hidden">Loading…</span>
                  </div>
                </div>
              )}

              {/* Empty / error state */}
              {!loading && !data && (
                <div className="text-center py-5">
                  <p className="text-muted mb-3">You don't have any previous orders to reorder.</p>
                  <Link
                    to="/wholesale/catalog"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onClose}
                  >
                    Browse Catalog
                  </Link>
                </div>
              )}

              {/* Item list */}
              {!loading && data?.items && (
                <>
                  {validationError && (
                    <div className="alert alert-warning py-2 mb-3" role="alert">
                      {validationError}
                    </div>
                  )}

                  <div className="d-flex flex-column gap-2">
                    {data.items.map(item => {
                      const src = imgSrc(item.imageUrl);
                      const inactive = !item.isActive;

                      return (
                        <div
                          key={item.productID}
                          className="d-flex align-items-center gap-3 p-2 border rounded"
                          style={{
                            opacity:         inactive ? 0.5 : 1,
                            background:      inactive ? '#f8f9fa' : '#fff',
                            pointerEvents:   inactive ? 'none' : 'auto',
                          }}
                        >
                          {/* Image */}
                          <div
                            style={{
                              width:        56,
                              height:       56,
                              minWidth:     56,
                              borderRadius: 4,
                              overflow:     'hidden',
                              background:   '#e9ecef',
                              flexShrink:   0,
                            }}
                          >
                            {src ? (
                              <img
                                src={src}
                                alt={item.proFanciful || item.proBrand}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                style={{
                                  width:      '100%',
                                  height:     '100%',
                                  background: '#dee2e6',
                                }}
                              />
                            )}
                          </div>

                          {/* Name + meta */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="fw-semibold text-truncate" style={{ fontSize: '0.9rem' }}>
                              {item.proFanciful || item.proBrand}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                              {[item.proVintage, item.proVarietal].filter(Boolean).join(' · ')}
                            </div>
                          </div>

                          {/* Price */}
                          <div
                            className="text-end"
                            style={{ minWidth: 72, fontSize: '0.85rem', fontWeight: 600 }}
                          >
                            {item.currentPrice != null
                              ? `$${Number(item.currentPrice).toFixed(2)}/cs`
                              : <span className="text-muted">—</span>
                            }
                          </div>

                          {/* Quantity or unavailable badge */}
                          <div style={{ minWidth: 120, textAlign: 'right' }}>
                            {inactive ? (
                              <span className="badge bg-secondary">No longer available</span>
                            ) : (
                              <input
                                type="number"
                                min="0"
                                className="form-control form-control-sm text-center"
                                style={{ width: 72, display: 'inline-block' }}
                                value={quantities[item.productID] ?? item.previousQty}
                                onChange={e => handleQtyChange(item.productID, e.target.value)}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              {!loading && data?.items && (
                <button
                  type="button"
                  className="btn btn-dark"
                  onClick={handleConfirm}
                >
                  <ShoppingCart size={15} className="me-1" />
                  Add All to Cart
                </button>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
